import React from "react";
import { AlertOctagon, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { reportClientError } from "@/lib/errorReporter";

/**
 * ErrorBoundary — catches render/lifecycle errors in the wrapped subtree so
 * a bug in one component (e.g. a stray null dereference during a big
 * enrichment poll) can't take down the entire tab.
 *
 * Chrome shows "Aw, Snap!" when the renderer process itself dies (usually
 * OOM). A React error boundary CAN'T catch that. What it CAN catch:
 *   • Uncaught exceptions during render / lifecycle
 *   • The cascading state-corruption that follows
 *
 * When triggered, we log to the console (so admins can grab the stack in
 * DevTools) and show a friendly "reload" pane.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary]", error, info?.componentStack);
    // Ship to the admin Errors tab (silent, deduped, rate-limited).
    reportClientError("react_render_crash", error?.message || String(error), {
      stack: error?.stack || "",
      context: {
        component_stack: info?.componentStack || "",
        url: typeof window !== "undefined" ? window.location.href : "",
      },
    });
  }

  handleReload = () => {
    // Hard reload — clears any corrupted in-memory state.
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div
        className="rounded-lg border border-rose-200 bg-rose-50 p-6 text-sm"
        data-testid="error-boundary-fallback"
        role="alert"
      >
        <div className="flex items-start gap-3">
          <AlertOctagon className="h-6 w-6 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-rose-900">
              Something went sideways on this page.
            </h3>
            <p className="text-rose-800 mt-1">
              The rest of the app is still fine — your background jobs
              (enrichments, imports, scans) are unaffected and continue on the
              server. Reload just this page to recover.
            </p>
            {this.state.error?.message ? (
              <pre className="mt-3 text-xs bg-rose-100 text-rose-900 rounded px-2 py-1 overflow-x-auto max-h-32">
                {this.state.error.message}
              </pre>
            ) : null}
            <div className="mt-3">
              <Button
                onClick={this.handleReload}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="error-boundary-reload"
              >
                <RotateCw className="h-4 w-4 mr-2" />
                Reload page
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
